/**
 * 消费原生注入的埋点上下文 —— 跨端会话关联的 web 半边。
 *
 * 原生在 documentStart 写 `window.__ED_TRACKING__`（KMP `trackingInitJs`），
 * 这里把其中的 session 维度写成 OpenReplay metadata，让 web session 能被
 * `app_run_id` 捞出来、并反指回原生 session。
 *
 * 规范：`docs/engineering/tracking/tracking-conventions.md` §7 traceId 模型。
 */

import { container, type NativeTrackingContext } from "./envelope";
import { Track } from "./index";
import { getOrCreateClientUuid } from "@utils/openreplay";

/** 上下文 → metadata key。值为空则跳过（不写空串，避免把「没有」写成「有但为空」）。 */
const CONTEXT_TO_METADATA: Array<[keyof NativeTrackingContext, string]> = [
  ["appRunId", "app_run_id"],
  ["platform", "platform"],
  ["env", "env"],
  ["appVersion", "app_version"],
  ["build", "build"],
  ["deviceModel", "device_model"],
  ["osVersion", "os_version"],
  ["locale", "locale"],
  ["plan", "plan"],
  ["nativeSessionId", "native_or_session"],
  // `adjustAdid` → `adjust_adid`：2026-08-18 运维把 `login_channel` 的槽位让给了
  // `ed_device_id` + `adjust_adid`，于是它**同时**是 session 维度（本表）和逐事件字段
  // （`envelope.ts` 的 `adjust_adid`）。两处都要：
  // envelope 让 CustomEvent 能按 adid group by，metadata 让 **MouseClick 也能按 adid 筛**
  // —— 点击消息在协议层没有 payload，envelope 帮不上它。
  ["adjustAdid", "adjust_adid"],
];

/**
 * web 自己就知道的 session 维度 —— **纯浏览器下也必须写**。
 *
 * 为什么不能只靠原生注入：`CONTEXT_TO_METADATA` 那批全部来自 `window.__ED_TRACKING__`，
 * 纯浏览器下那个对象不存在 → 一个 key 都不写 → **浏览器 session 分不出环境**。
 * 而 OpenReplay 事件行自带的 `environment` 列在我们这套实例上永远是 `N/A`
 * （移动 SDK 不发这个字段；web 侧同样没有），所以只有我们自己写才有。
 *
 * `container` 走 `browser` / `webview`：与 envelope 里的同名字段同源（`container()`），
 * 让「这条 session 是不是 App 内的」在 session 维度也能筛。
 */
function ownMetadata(): Record<string, string> {
  const patch: Record<string, string> = {};
  const env = process.env.NEXT_PUBLIC_ENV;
  if (env) {
    patch.env = env;
  }
  patch.container = container();
  const rev = process.env.NEXT_PUBLIC_WEB_REV;
  if (rev) {
    patch.web_rev = rev;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    patch.locale = navigator.language;
  }
  // `ed_device_id`：与 envelope 里逐事件带的**同一个来源**（logbeacon 的 client uuid，localStorage 持久）。
  // 2026-08-18 槽位声明后才真正落库。写成 session 级的理由与 native 一致 ——
  // **web 的 `MouseClick`(68) 同样没有 payload**，按设备筛点击只能靠 session 的 metadata。
  //
  // ⚠️ **无条件写，与登录态无关**：envelope 那份在登录后会让位给 `owner_id`
  // （`setEnvelopeIdentity(ownerId ? null : userId, ownerId)`），但 session 维度不该跟着变 ——
  // 「这条 session 出自哪台设备」登录前后是同一个答案。
  try {
    const deviceId = getOrCreateClientUuid();
    if (deviceId) {
      patch.ed_device_id = deviceId;
    }
  } catch {
    // SSR / 隐私模式下拿不到 —— 跳过即可，缺一个 session 维度不影响业务
  }
  return patch;
}

/**
 * `traceId` **不写 metadata** —— 它是每次跳转的粒度，写成 session 级会被后一次跳转覆盖，
 * 变成"整个 session 只有最后一次跳转的 trace"。它由 `envelope()` 逐事件带在 payload 里。
 *
 * 原生注入值**优先于** web 自己算的（App 内 `platform`/`env` 以壳为准）。
 */
export function applyNativeContext(ctx?: NativeTrackingContext): string[] {
  const patch: Record<string, string> = ownMetadata();
  for (const [field, key] of CONTEXT_TO_METADATA) {
    const value = ctx?.[field];
    if (typeof value === "string" && value.length > 0) {
      patch[key] = value;
    }
  }
  Track.context(patch);
  return Object.keys(patch);
}

/** 读注入值；不在 App WebView 里时返回 undefined。 */
export function nativeTrackingContext(): NativeTrackingContext | undefined {
  return typeof window === "undefined" ? undefined : window.__ED_TRACKING__;
}

/**
 * 合并原生**后补**的上下文（桥消息 `tracking-context`）。
 *
 * 为什么需要补推：
 * 1. **预热的 WebView 早于原生 session 建立** → 注入时 `nativeSessionId` 还是空的；
 * 2. **池复用同一个 WebView** → `traceId` 变了，但 documentStart 不会重跑。
 *
 * 两种情况都由原生侧推一条 partial 上下文过来，这里合并进 `window.__ED_TRACKING__`
 * 并补写 metadata。**就地改对象**，因为 `envelope()` 每次都现读它取 `traceId`。
 */
export function mergeNativeContext(
  patch: Partial<NativeTrackingContext>,
): string[] {
  if (typeof window === "undefined" || !patch) {
    return [];
  }
  const merged: NativeTrackingContext = { ...window.__ED_TRACKING__, ...patch };
  window.__ED_TRACKING__ = merged;
  return applyNativeContext(merged);
}
